/**
 * Hva vi lagrer i nettleseren, og hva den besøkende har valgt om det.
 *
 * Vår Dag setter tre informasjonskapsler, og alle tre er nødvendige for at
 * tjenesten skal virke i det hele tatt:
 *
 * - sesjonen din når du er logget inn
 * - sesjonen til administrasjonsportalen
 * - beviset på at du kom inn bak passordet mens siden bygges
 *
 * Ingen av de tre kan skrus av uten at siden slutter å virke. Etter ekomloven
 * § 3-15 kreves det *samtykke* bare for det som ikke er nødvendig for tjenesten
 * brukeren har bedt om; for de nødvendige kreves *informasjon*.
 *
 * Nå finnes det to ting som ikke er nødvendige, og boksen har derfor ekte valg:
 *
 * - **Annonsemålingen.** Kommer noen inn på en annonselenke, bærer den med seg
 *   en klikk-ID – `gclid` fra Google, `fbclid` fra Meta – som finnes for å
 *   kunne meldes tilbake til annonsenettverket som en konvertering. Juridisk
 *   gjennomgang landet på at den krever samtykke, både for å lagres på maskinen
 *   og for å festes til kontoen.
 * - **Besøksstatistikken.** En tilfeldig ID i nettleseren, sendt med målingen
 *   av de åpne sidene. Den er det eneste som kan skille 28 besøk fra fem
 *   personer som kommer igjen – og å kjenne igjen en nettleser krever et ja.
 *   Se `besoksId` i `lib/trakt.ts`.
 *
 * Kanal og kampanjenavn er noe annet. De sier at noen kom fra Google eller fra
 * en kampanje, aldri hvilket klikk, og hviler på berettiget interesse. De
 * lagres uansett svar – og står beskrevet i erklæringen, med en frist.
 *
 * Selve valget lagres her, i nettleserens eget lager. Det er nødvendig for å
 * kunne respektere det: uten et sted å skrive svaret ville vi spurt på nytt ved
 * hver sidelasting, og ikke kunnet la være å måle den som sa nei.
 */

const NOKKEL = 'vaardag:informasjonskapsler';

/**
 * Økes når beskjeden folk har lest, ikke lenger stemmer.
 *
 * Et gammelt valg regnes da som ubesvart, og boksen kommer fram igjen – ellers
 * ville de som svarte i fjor aldri fått høre om det som kom i år. To ting
 * utløser det: at vi begynner å lagre noe nytt, eller at det vi allerede har
 * fortalt, viser seg å være galt.
 *
 * 2: beskjeden lovte at vi verken sporet eller delte noe. Det stemte ikke
 *    lenger etter at kartet ble hentet fra Google og driftsmålingen lagt til
 *    Microsoft. De som rakk å lese løftet, skal få se rettelsen.
 * 3: boksen ba ikke om noe. Nå finnes annonsemålingen, og den skal det sies ja
 *    eller nei til. Et «greit» fra den gamle boksen er ikke et samtykke til
 *    noe som ikke fantes da det ble gitt.
 * 4: besøksstatistikken. Samme grunn: et ja til annonsemåling er ikke et ja til
 *    å bli kjent igjen fra besøk til besøk.
 */
export const GJELDENDE_VERSJON = 4;

export type Kapselvalg = {
  versjon: number;
  /** Når brukeren svarte. Et samtykke skal kunne påvises, også dette. */
  tidspunkt: string;
  /**
   * Om vi får måle annonser: lagre klikk-ID-en, og senere melde kjøpet tilbake
   * til nettverket annonsen kom fra. `false` er svaret til den som ikke har
   * valgt, og til den som har sagt nei – de to skilles av at valget mangler.
   */
  annonsemaaling: boolean;
  /**
   * Om vi får kjenne igjen nettleseren fra besøk til besøk, med en tilfeldig
   * ID. Uten et ja lages ingen ID, og besøkene telles bare som en sum.
   */
  statistikk: boolean;
};

/** Det som kan velges. Tidspunktet og versjonen setter vi selv. */
export type Valg = Pick<Kapselvalg, 'annonsemaaling' | 'statistikk'>;

/**
 * Hendelsene boksen lytter etter.
 *
 * Valget kan endres fra flere steder – boksen selv, personvernsida og
 * innstillingene – og boksen må vite om det. `ENDRE` ber den komme fram igjen
 * med valget som står; `LAGRET` sier at det er svart et annet sted, så den kan
 * gå bort.
 */
export const ENDRE = 'vaardag:informasjonskapsler-endre';
export const LAGRET = 'vaardag:informasjonskapsler-lagret';

export function lesValg(): Kapselvalg | null {
  try {
    const lagret = window.localStorage.getItem(NOKKEL);
    if (!lagret) return null;
    const valg = JSON.parse(lagret) as Partial<Kapselvalg>;
    if (typeof valg?.versjon !== 'number' || valg.versjon < GJELDENDE_VERSJON) return null;
    return {
      versjon: valg.versjon,
      tidspunkt: String(valg.tidspunkt ?? ''),
      annonsemaaling: Boolean(valg.annonsemaaling),
      statistikk: Boolean(valg.statistikk),
    };
  } catch {
    // Privat modus og strenge nettleserinnstillinger kan nekte oss lagring.
    // Da er svaret «vi vet ikke», og boksen vises igjen. Det er riktigere enn
    // å la som om vi har fått et svar vi ikke har.
    return null;
  }
}

/**
 * Om annonsemålingen er tillatt.
 *
 * Alt som ikke er et uttrykkelig ja, er et nei: den som ikke har svart, den som
 * har sagt nei, og den som har skrudd av lagring i nettleseren. Et samtykke
 * man ikke kan lese, er ikke gitt.
 */
export function harAnnonsemaaling(): boolean {
  return lesValg()?.annonsemaaling === true;
}

/** Om besøksstatistikken er tillatt. Samme regel: alt som ikke er et ja, er et nei. */
export function harStatistikk(): boolean {
  return lesValg()?.statistikk === true;
}

export function lagreValg(valg: Valg): void {
  try {
    window.localStorage.setItem(
      NOKKEL,
      JSON.stringify({
        versjon: GJELDENDE_VERSJON,
        tidspunkt: new Date().toISOString(),
        annonsemaaling: valg.annonsemaaling,
        statistikk: valg.statistikk,
      } satisfies Kapselvalg),
    );
  } catch {
    /* Klarer vi ikke å lagre, kommer boksen igjen neste gang. Ikke verdt en feilmelding. */
  }
  try {
    window.dispatchEvent(new Event(LAGRET));
  } catch {
    /* Uten vindu er det ingen boks å si fra til. */
  }
}

/**
 * Endrer ett av valgene og lar det andre stå.
 *
 * Er ingenting valgt fra før, er det andre et nei – det er svaret til den som
 * ikke har sagt ja.
 */
export function endreValg(endring: Partial<Valg>): void {
  const naa = lesValg();
  lagreValg({
    annonsemaaling: naa?.annonsemaaling ?? false,
    statistikk: naa?.statistikk ?? false,
    ...endring,
  });
}

/** Ber boksen komme fram igjen, så valget kan endres. */
export function aapneValget(): void {
  window.dispatchEvent(new Event(ENDRE));
}
